import React from "react";

import { isEmpty } from "lodash/fp";

import { AvatarGroup, SxProps, Typography } from "@mui/material";

import { CustomTooltipWrapper } from "@app/analytics/Pulse/components/styles";
import type { PulseContributor } from "@app/analytics/Pulse/types";
import { getInitials } from "@app/common/utils";

import Avatar from "@rui/Avatar";
import Stack, { Row } from "@rui/Stack";

const Contributors = ({
  contributors,
  maxVisible,
  sx,
}: {
  contributors: PulseContributor[];
  /** Max number that will show. If more than this number of contributors, will show a "+N" avatar */
  maxVisible: number;
  sx?: SxProps;
}) => {
  if (isEmpty(contributors)) return null;

  const visibleContributors = contributors.slice(0, maxVisible);
  const additionalContributors = contributors.slice(maxVisible);

  return (
    <CustomTooltipWrapper
      minWidth={200}
      title={
        <Stack padding={1} spacing={1}>
          <Stack spacing={0.25} sx={{ textAlign: "left" }}>
            <Typography variant="bodySecondaryMed" color="shades.neutral.800">
              Contributors ({contributors.length})
            </Typography>
            <Typography variant="labelReg" color="shades.neutral.400">
              People invited to meetings that contributed to this topic
            </Typography>
          </Stack>
          <Stack spacing={1}>
            {contributors.map((c) => (
              <Row variant="left" key={c.name} spacing={1}>
                <Avatar name={c.name}>{getInitials(c.name)}</Avatar>
                <Typography
                  variant="bodySecondaryReg"
                  color="shades.neutral.600"
                  sx={{
                    textAlign: "left",
                    fontFamily: (theme) => theme.typography.fontFamilySecondary,
                  }}
                >
                  {c.name}
                </Typography>
              </Row>
            ))}
          </Stack>
        </Stack>
      }
    >
      <AvatarGroup
        sx={{
          justifyContent: "flex-end",
          "& .MuiAvatarGroup-avatar": { borderWidth: "1px" },
          ...sx,
        }}
        max={maxVisible + 1}
      >
        {visibleContributors.map((c) => (
          <Avatar key={c.name} name={c.name}>
            {getInitials(c.name)}
          </Avatar>
        ))}
        {!isEmpty(additionalContributors) && (
          <Avatar
            sx={{ bgcolor: "shades.neutral.100", color: "shades.neutral.500" }}
          >
            +{additionalContributors.length}
          </Avatar>
        )}
      </AvatarGroup>
    </CustomTooltipWrapper>
  );
};

export default Contributors;
